import { type ProColumns } from '@ant-design/pro-components'
import { Link } from '@tanstack/react-router'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { type RentalOrder } from '@super-mega/server/contracts/order'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { TABLE_ACTION_COLUMN } from '@/const/list.ts'
import type { useMemberCreatorOptions } from '@/features/access/index.ts'
import { OrderContractCountCell } from '@/features/contract/index.ts'
import { BusinessTag } from '@/features/master-data/index.ts'
import {
  LEASE_ACTION_COLORS,
  LEASE_ACTION_LABELS,
  OCCUPANCY_MODE_COLORS,
  OCCUPANCY_MODE_LABELS
} from './const/rentals-index-list.ts'
import type { useOrderListFilterOptions } from './hooks/use-order-list-filter-options.ts'
import { OrderSubjectCell } from './order-list-cells.tsx'
import {
  createOrderListAuditColumns,
  createOrderListBaseColumns,
  ORDER_LIST_COLUMN_WIDTHS
} from './order-list-columns.ts'
import { RENTAL_STATUS, type RentalListSearch } from './rental-list-search.ts'

dayjs.extend(utc)

type RentalColumnsOptions = {
  search: RentalListSearch
  memberCreator: ReturnType<typeof useMemberCreatorOptions>
  canManage: boolean
  canSubmit: boolean
  canReadBills: boolean
  isSuperAdmin: boolean
  isPending: boolean
  onView: (order: RentalOrder) => void
  onContracts: (order: RentalOrder) => void
  onBills: (order: RentalOrder) => void
  onEdit: (order: RentalOrder) => void
  onOpenContract: (order: RentalOrder) => void
  onArchive: (order: RentalOrder) => void
  onRemove: (order: RentalOrder) => void
  onForceRemove: (order: RentalOrder) => void
  filterOptions: ReturnType<typeof useOrderListFilterOptions>
}

export function createRentalColumns({
  search,
  memberCreator,
  canManage,
  canSubmit,
  canReadBills,
  isSuperAdmin,
  isPending,
  onView,
  onBills,
  onContracts,
  onEdit,
  onOpenContract,
  onArchive,
  onRemove,
  onForceRemove,
  filterOptions
}: RentalColumnsOptions): ProColumns<RentalOrder>[] {
  return [
    ...createOrderListBaseColumns<RentalOrder>({
      keywordPlaceholder: '订单号、ID 或备注',
      filterOptions,
      statusMap: RENTAL_STATUS,
      renderOrderNumber: (order) => (
        <Link
          className="text-content! hover:text-accent! font-semibold underline! underline-offset-2 transition-colors"
          title="在新标签页查看订单详情"
          to="/orders/rentals/$rentalId"
          params={{ rentalId: String(order.id) }}
          search={search}
          target="_blank"
          rel="noopener noreferrer"
        >
          {order.orderNo}
        </Link>
      ),
      renderOrderGroup: (order) => {
        if (!order.orderGroupId) return undefined
        return (
          <Link
            className="hover:text-accent! text-inherit! underline! underline-offset-2 transition-colors"
            title="在新标签页查看订单组"
            to="/orders/groups/$groupId"
            params={{ groupId: String(order.orderGroupId) }}
            search={{ kind: 'rental' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {order.orderGroupName || order.orderGroupId}
          </Link>
        )
      },
      renderCustomer: (order) => (
        <Link
          className="text-content! hover:text-accent! underline! underline-offset-2 transition-colors"
          title="在新标签页查看客户资料"
          to="/customer-management/customers/$customerId/documents"
          params={{ customerId: String(order.customerId) }}
          search={{ kind: 'rental' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {order.customerName || '—'}
        </Link>
      ),
      renderSubject: (order) => (
        <OrderSubjectCell
          primary={order.companyName || order.subjectName}
          secondary={order.identityName ? `法人：${order.identityName}` : null}
        />
      )
    }),
    {
      title: '服务对象关联',
      dataIndex: 'subjectAssociation',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        options: [
          { label: '未关联', value: 'unlinked' },
          { label: '已关联', value: 'linked' }
        ]
      }
    },
    {
      title: '业务类型',
      dataIndex: 'businessName',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.businessType,
      render: (_, order) => <BusinessTag businessColor={order.businessColor} businessName={order.businessName} />
    },
    {
      title: '租赁地址',
      search: false,
      key: 'address',
      width: ORDER_LIST_COLUMN_WIDTHS.rentalAddress,
      render: (_, order) => `${order.siteName} ${order.roomNo ?? ''}`
    },
    {
      title: '租期',
      search: false,
      key: 'period',
      width: ORDER_LIST_COLUMN_WIDTHS.rentalPeriod,
      render: (_, order) =>
        `${dayjs.utc(order.startAt).format('YYYY-MM-DD')} 至 ${dayjs.utc(order.endAt).format('YYYY-MM-DD')}`
    },
    {
      title: '使用方式',
      dataIndex: 'occupancyMode',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.occupancyMode,
      render: (_, order) => (
        <Tag color={OCCUPANCY_MODE_COLORS[order.occupancyMode]}>{OCCUPANCY_MODE_LABELS[order.occupancyMode]}</Tag>
      )
    },
    {
      title: '办理情形',
      dataIndex: 'leaseAction',
      search: false,
      width: ORDER_LIST_COLUMN_WIDTHS.leaseAction,
      render: (_, order) => (
        <Tag color={LEASE_ACTION_COLORS[order.leaseAction]}>{LEASE_ACTION_LABELS[order.leaseAction]}</Tag>
      )
    },
    {
      title: '合同文件',
      dataIndex: 'contractDocumentCount',
      search: false,
      width: '8rem',
      render: (_, order) => (
        <OrderContractCountCell order={{ ...order, type: 'rental' }} onOpen={() => onContracts(order)} />
      )
    },
    ...createOrderListAuditColumns<RentalOrder>({ search, memberCreator }),
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, row) => {
        const actions: TableRowAction[] = [{ key: 'view', label: '查看', onClick: () => onView(row) }]
        if (
          !row.archivedAt &&
          !['ended', 'cancelled', 'archived'].includes(row.status) &&
          (canManage || (row.status === 'pending' && canSubmit))
        ) {
          actions.push({
            key: 'edit',
            label: row.status === 'archived' ? '补充资料' : '编辑订单',
            onClick: () => onEdit(row),
            disabled: isPending
          })
        }
        if (canReadBills) {
          actions.push({
            key: 'bills',
            label: '查看账单',
            onClick: () => onBills(row)
          })
        }
        actions.push({ key: 'contracts', label: '查看合同文件', onClick: () => onContracts(row) })
        actions.push({ key: 'contract', label: '打印合同', onClick: () => onOpenContract(row) })
        if (canManage && !row.archivedAt && ['ended', 'cancelled'].includes(row.status)) {
          actions.push({ key: 'archive', label: '归档', onClick: () => onArchive(row), disabled: isPending })
        }
        if (canManage) {
          const canDelete = isSuperAdmin || row.status === 'pending' || !!row.archivedAt || row.status === 'archived'
          actions.push({
            key: 'remove',
            label: '删除订单',
            danger: true,
            onClick: () => onRemove(row),
            disabled: isPending || !canDelete
          })
        }
        if (isSuperAdmin) {
          actions.push({
            key: 'forceRemove',
            label: '强制删除',
            danger: true,
            disabled: isPending,
            onClick: () => onForceRemove(row)
          })
        }
        return <TableRowActions resource="order.rental" actions={actions} maxVisibleActions={1} />
      }
    }
  ]
}
